import { useQuery } from '@tanstack/react-query';
import { getAuthenticatedClient } from '@/services/client';

// Home-screen daily task ring. Reflects what the farmer has actually
// logged today for a given batch, derived from the source tables each
// log screen writes to:
//   - feed:         daily_records.feed_consumed_kg > 0
//   - mortality:    daily_records.mortality_count > 0 OR mortality_cause IS NOT NULL
//   - vaccination:  any vaccination_records row with vaccination_date = today
//   - biosecurity:  any photo with photo_category IN ('shed','equipment') created today
//                   (BiosecurityLogScreen does not write to daily_records; photos are
//                    the only DB trace it leaves.)
//
// The four task ids returned here MUST match the ids in
// HomeScreen's taskCards array: 'vaccination', 'mortality', 'feed',
// 'biosecurity'. Total is fixed at 4 tasks to keep the ring stable.

export const TOTAL_TASKS = 4;

export type TaskId = 'vaccination' | 'mortality' | 'feed' | 'biosecurity';

interface TaskStatus {
  completedTaskIds: TaskId[];
  totalTasks: number;
}

const EMPTY_STATUS: TaskStatus = {
  completedTaskIds: [],
  totalTasks: TOTAL_TASKS,
};

export const useTodayTaskStatus = (batchId: string | undefined): TaskStatus => {
  const today = new Date().toISOString().split('T')[0];
  // Match the same UTC-day window that daily_records / vaccination_records
  // compute with new Date().toISOString().split('T')[0]:
  const todayStartUtc = `${today}T00:00:00.000Z`;
  const tomorrowStartUtc = new Date(
    new Date(todayStartUtc).getTime() + 86400000,
  ).toISOString();

  const { data } = useQuery<TaskStatus>({
    queryKey: ['cbs', 'today-task-status', batchId, today],
    enabled: !!batchId,
    staleTime: 1000 * 60 * 2,
    queryFn: async () => {
      if (!batchId) return EMPTY_STATUS;
      const client = getAuthenticatedClient();

      const [dailyRes, vaccRes, photoRes] = await Promise.all([
        client
          .from('daily_records')
          .select('feed_consumed_kg, mortality_count, mortality_cause')
          .eq('batch_id', batchId)
          .eq('record_date', today)
          .maybeSingle(),
        client
          .from('vaccination_records')
          .select('id', { count: 'exact', head: true })
          .eq('batch_id', batchId)
          .eq('vaccination_date', today),
        client
          .from('photos')
          .select('id', { count: 'exact', head: true })
          .eq('batch_id', batchId)
          .in('photo_category', ['shed', 'equipment'])
          .gte('created_at', todayStartUtc)
          .lt('created_at', tomorrowStartUtc),
      ]);

      const completed: TaskId[] = [];

      const dailyRow = dailyRes.data;
      if (dailyRow) {
        const feedKg = Number(dailyRow.feed_consumed_kg ?? 0);
        if (feedKg > 0) completed.push('feed');
        const mortalityCount = Number(dailyRow.mortality_count ?? 0);
        const mortalityCause = dailyRow.mortality_cause;
        if (mortalityCount > 0 || (mortalityCause != null && mortalityCause !== '')) {
          completed.push('mortality');
        }
      }

      if ((vaccRes.count ?? 0) > 0) completed.push('vaccination');
      if ((photoRes.count ?? 0) > 0) completed.push('biosecurity');

      return { completedTaskIds: completed, totalTasks: TOTAL_TASKS };
    },
  });

  return data ?? EMPTY_STATUS;
};
